import React, { FC, useState } from 'react';
import { IoBriefcaseOutline, IoClose } from 'react-icons/io5';

import { ActionIcon, Box, Menu, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

import { PublishedStatus } from 'models';

import { InlineFiltersSection } from 'components/inline-filters/InlineFilters.Section';
import { useCompanyProfiles } from 'hooks/use-company-profiles';

import cx from './InlineFilters.Manufacturers.module.scss';

type Props = {
    label?: string;
    isViewOnly?: boolean;
    isRequired?: boolean;
    onSelect: (id: string) => void;
    onRemove: () => void;
    selectedManufacturerIds?: string | string[];
};

const InlineFiltersManufacturerComponent: FC<Props> = ({
    label = 'Manufacturer',
    isViewOnly,
    isRequired,
    onSelect,
    onRemove,
    selectedManufacturerIds,
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);

    const { companies: selectedManufacturers } = useCompanyProfiles({
        ids: typeof selectedManufacturerIds === 'string' ? [selectedManufacturerIds] : selectedManufacturerIds,
        execute: !!selectedManufacturerIds && !!selectedManufacturerIds.length,
        keepPreviousData: false,
    });

    const { companies: manufacturers, isLoading } = useCompanyProfiles({
        query: debouncedSearch,
        limit: 24,
        status: PublishedStatus.PUBLISHED,
        select: {
            name: true,
        },
        execute: !!debouncedSearch,
    });

    return (
        <InlineFiltersSection
            isViewOnly={isViewOnly}
            active={!!selectedManufacturers.length}
            icon={<IoBriefcaseOutline size={12} />}
            label={selectedManufacturers.length ? undefined : label}
            body={selectedManufacturers ? selectedManufacturers.map(({ name }) => name).join(', ') : undefined}
            onRemove={selectedManufacturers.length && !isRequired ? onRemove : undefined}
            popoverContent={
                <Box className={cx.options}>
                    <TextInput
                        value={search}
                        className={cx.search}
                        placeholder="Search by name"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.currentTarget.value)}
                        rightSection={
                            search && (
                                <ActionIcon onClick={() => setSearch('')} size="xs" variant="light">
                                    <IoClose />
                                </ActionIcon>
                            )
                        }
                    />

                    {isLoading && (
                        <Menu.Item disabled>
                            <Text c="dimmed">Loading...</Text>
                        </Menu.Item>
                    )}

                    {manufacturers.map((manufacturer) => (
                        <Menu.Item
                            key={manufacturer.id}
                            title={manufacturer.name}
                            classNames={{ itemLabel: cx.optionLabel }}
                            onClick={() => onSelect(manufacturer.id)}
                        >
                            <Text inherit truncate>
                                {manufacturer.name}
                            </Text>
                        </Menu.Item>
                    ))}

                    {!manufacturers.length && !isLoading && (
                        <>
                            {debouncedSearch ? (
                                <Menu.Item disabled>
                                    <Text c="dimmed">No companies found</Text>
                                </Menu.Item>
                            ) : (
                                <Menu.Item disabled>
                                    <Text c="dimmed">Search for a company</Text>
                                </Menu.Item>
                            )}
                        </>
                    )}
                </Box>
            }
        />
    );
};

export { InlineFiltersManufacturerComponent };
